<template>
  <div class="dashboard-home p-4">
    <!-- Header with Quick Stats -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div v-for="stat in quickStats"
           :key="stat.title"
           class="stat-card p-4 rounded-lg dark:bg-gray-700 shadow">
        <h3 class="flex items-center justify-between">
          <a class="dark:text-gray-100 text-sm" :href="stat.link">
            {{ stat.title }}
          </a>
          <div>
            <i :class="'w-4 h-4 ph  ph-' + stat.icon" v-if="typeof (stat.icon) === 'string' "></i>
            <component :is="stat.icon" class="w-5 h-5" v-else/>
          </div>

        </h3>

        <span class="text-2xl font-semibold">{{ data.quick_stats[stat.key] }}</span>


      </div>
    </div>

    <!-- Machine Status Distribution & Maintenance Need -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <div class="card p-4 bg-gray-400 rounded-lg shadow">
        <h3 class="text-lg font-semibold mb-4">Machine Status Distribution</h3>
        <div ref="statusChart"></div>
      </div>

      <div class="card p-4  rounded-lg shadow">
        <h3 class="text-lg font-semibold mb-4">Upcoming Inspections</h3>
        <div class="space-y-4">
          <div v-for="machine in data.machines_need_inspection"
               :key="machine.id"
               class="flex items-center justify-between p-2 dark:hover:bg-gray-500 dark:hover:text-white  rounded">
            <div>
              <p class="font-medium">{{ machine.name }}</p>
              <p class="text-sm text-gray-500 ">Due: {{ formatDate(machine.next_inspection_date) }}</p>
            </div>
            <a :href="`/dashboard/organizations/${route.params.organization_uuid}/machines/${machine.id}/maintenance_schedules/new`"
               class="btn btn-primary btn-sm">
              Re-Schedule
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Sites Map & Customer Overview -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <!-- Sites Map -->
      <div class=" card py-4  rounded-lg shadow max-h-[200px] overflow-scroll">
        <h3 class="text-lg font-semibold mb-4">Sites Overview</h3>
        --
        <div v-for="site in data.sites">
          <p class="font-medium">{{ site.name }}</p>
        </div>
        <div class="h-[400px] relative">
          <div id="sitesMap" class="h-full w-full"></div>
        </div>
      </div>

      <!-- Customers List -->
      <div class="card py-4  rounded-lg shadow">
        <h3 class="text-lg font-semibold mb-4">Top Customers</h3>
        <div class="space-y-4">
          <div v-for="customer in data.top_customers"
               :key="customer.id"
               class="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
            <div>
              <p class="font-medium">{{ customer.name }}</p>
              <p class="text-sm text-gray-500">{{ customer.machines_count }} machines</p>
            </div>
            <RouterLink :to="`/customers/${customer.id}`"
                        class="text-blue-500 hover:text-blue-700">
              View →
            </RouterLink>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Activity Timeline -->
    <div class="card p-4  rounded-lg shadow">
      <h3 class="text-lg font-semibold mb-4">Recent Activity</h3>
      <div class="space-y-4">
        <div v-for="activity in data.recent_activities"
             :key="activity.id"
             class="flex items-start space-x-3 p-2">
          <div :class="getActivityIconClass(activity.type)" class="rounded-full p-2">
            <i :class="`ph-${getActivityIcon(activity.type)}`"></i>
          </div>
          <div>
            <p class="font-medium">{{ activity.description }}</p>
            <p class="text-sm text-gray-500">{{ formatDate(activity.created_at) }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import "@phosphor-icons/vue";
import {
  PhHorse,
  PhBulldozer,
  PhCube,
  PhExclamationMark,
  PhBinoculars,
  PhUsers,
  PhAddressBook, PhPipeWrench, PhLinkBreak, PhAlarm
} from "@phosphor-icons/vue";

import {ref, onMounted} from 'vue'
import ApexCharts from 'apexcharts'
import mapboxgl from 'mapbox-gl'
import {useRoute} from "vue-router";

const route = useRoute();
const uuid = route.params.organization_uuid;
const quickStats = ref([
  {
    title: 'Fleet / Machines', key: "machines_count",
    value: 0,
    trend: 0,
    icon: PhBulldozer,
    link: `/dashboard/organizations/${uuid}/fleet`,
  },
  {
    title: 'Fleet in risk', key: "machines_in_risk",
    value: 0,
    trend: 0,
    icon: PhExclamationMark,
    link: `/dashboard/organizations/${uuid}/fleet?status=need_repair`,
  },
  {
    title: 'Signals',
    key: "signals_count",
    value: 0,
    trend: 0,
    icon: PhExclamationMark,
    link: `/dashboard/organizations/${uuid}/fleet?status=need_repair`,
  },
  {
    title: 'Fleet need inspection', key: "machines_need_inspection",
    value: 0,
    trend: 0,
    icon: PhBinoculars,
    link: `/dashboard/organizations/${uuid}/fleet?q=&status%5B%5D=1&status%5B%5D=9`,
  },
  {title: 'Active Sites', key: "active_sites", value: 0, trend: 0, icon: 'map'},
  {title: 'Critical incidents', key: "critical_incidents", value: 0, trend: 0,

    icon: PhAlarm},
  {
    title: 'Customers', key: "customers_count", value: 0, trend: 0,

    link: `/dashboard/organizations/${uuid}/customers`,
    icon: PhAddressBook
  },
  {
    title: 'Pending Inspections',
    key: "pending_inspections",
    value: 0,
    trend: 0,
    link: `/dashboard/organizations/${uuid}/inspections`,
    icon: PhPipeWrench
  },
  {
    title: 'Incidents',
    link: `/dashboard/organizations/${uuid}/machine_incidents`,
    key: "incidents_count",
    value: 0,
    trend: 0,
    icon: PhLinkBreak
  },
]);
const data = ref({
  quick_stats: [],
  machines_need_inspection: [],
  machines_in_risk: [],
  top_customers: [],
  recent_activities: [],
  sites: []
})

// State
const statusChart = ref(null)
// Methods
const initializeStatusChart = async () => {
  try {
    const response = await fetch(`/dashboard/organizations/${uuid}/fleet/status.json`)
    const statues = (await response.json()).data.statues;
    var options = {
      labels: Object.keys(statues),
      series: Object.values(statues),
      chart: {
        height: 350,
        type: 'donut',
      },
      dataLabels: {
        enabled: false
      },
      colors: ['#10B981', '#FBBF24', '#EF4444', '#6366F1', '#636201'],
      title: {
        // text: 'HeatMap Chart (Single color)'
      },
    };
    var chart = new ApexCharts(statusChart.value, options);
    chart.render();
  } catch (error) {
    console.error('Error fetching dashboard data:', error)
  }
};
// TODO , add map overview
const initializeSitesMap = () => {
  /* mapboxgl.accessToken = 'pk.eyJ1IjoiaG91c3NlbS1mYXRoYWxsYWgiLCJhIjoiY21lMWw2Y2pzMDlydzJqcHA5cmcybWFmdyJ9.P32nDlNPSnYa_Pq7jQY9vw'
   const map = new mapboxgl.Map({
     container: 'sitesMap',
     style: 'mapbox://styles/mapbox/light-v10',
     center: [-96, 37.8],
     zoom: 3
   })

   // Add markers for each site
   data.value.sites.forEach(site => {
     new mapboxgl.Marker()
         .setLngLat([site.longitude, site.latitude])
         .setPopup(new mapboxgl.Popup().setHTML(`
         <h3 class="font-medium">${site.name}</h3>
         <p>${site.machines_count} machines</p>
       `))
         .addTo(map)
   })
   */
}

const fetchDashboardData = async () => {
  try {
    const response = await fetch(`/dashboard/organizations/${uuid}.json`)
    const jsonData = await response.json();
    data.value = jsonData.data;
  } catch (error) {
    console.error('Error fetching dashboard data:', error)
  }
}

const getActivityIcon = (type) => {
  return 'ph-hat';
}

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getActivityIconClass = (type) => {
  const classes = {
    maintenance: 'bg-blue-500',
    inspection: 'bg-purple-500',
    incident: 'bg-red-500',
    repair: 'bg-yellow-500'
  }
  return classes[type] || 'bg-gray-500'
}

const scheduleInspection = (machine) => {
  // Implementation for scheduling inspection
}

// Lifecycle
onMounted(() => {
  fetchDashboardData()
  initializeStatusChart()
  initializeSitesMap()
})
</script>

<style>
@import 'mapbox-gl/dist/mapbox-gl.css';

.stat-card {
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}
</style>